import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
type Bonus = {
  num: string;
  title: string;
  desc: string;
  image: string | null;
};
const bonuses: Bonus[] = [{
  num: 'I',
  title: 'Biblioteca de Interpretaciones Reales',
  desc: 'Accedé a una colección exclusiva de interpretaciones reales de cartas natales, organizadas para aprender cómo analiza un profesional cada caso paso a paso.',
  image: 'https://cdn.magicpatterns.com/uploads/2NhMLyxowTbzB67vJwAJ1i/Untitled_design_(6).png'
}, {
  num: 'II',
  title: 'Protocolo de Apertura de Carta Natal',
  desc: 'El sistema exacto para saber qué mirar primero en segundos.',
  image: 'https://cdn.magicpatterns.com/uploads/3dyMfz8xcL81G9voaGTLGH/Untitled_design_(7).png'
}, {
  num: 'III',
  title: 'Plantillas Profesionales de Sesiones',
  desc: 'Estructuras listas para entregar sesiones claras, profundas y memorables.',
  image: 'https://cdn.magicpatterns.com/uploads/a2xvgJzCBWGNVgon6pb92S/PLANTILLAS_PROFESIONALES_DE_SESSIONES_2.png'
}, {
  num: 'IV',
  title: 'Framework de Interpretación Profesional',
  desc: 'Aprendé a interpretar con orden, coherencia y seguridad real.',
  image: 'https://cdn.magicpatterns.com/uploads/5q7cwNcxRQLw86VRsodSzx/FRAMEWORK.png'
}, {
  num: 'V',
  title: 'Mapa Mental Completo del Sistema',
  desc: 'Toda la metodología resumida visualmente para conectar información más rápido.',
  image: 'https://cdn.magicpatterns.com/uploads/uf2d9761743C9gPifsnBez/MAPA_MENTAL_COMPLETO_2.png'
}, {
  num: 'VI',
  title: 'Guión Profesional de Interpretación',
  desc: 'La estructura exacta para interpretar sin bloquearte frente a una carta real.',
  image: 'https://cdn.magicpatterns.com/uploads/rotaULN7R8pWareSjYA99h/GUION_PROFESIONAL_DE_INTERPRETACION_2.png'
}, {
  num: 'VII',
  title: 'Sistema de Sesiones Rentables',
  desc: 'Aprendé a estructurar servicios astrológicos más claros y profesionales.',
  image: 'https://cdn.magicpatterns.com/uploads/54NRChUQ5D5nzS4g4PipH2/SISTEMA_DE_SESIONES_RENTABLES.png'
}, {
  num: 'VIII',
  title: 'Banco de Preguntas para Sesiones Profundas',
  desc: 'Preguntas diseñadas para crear interpretaciones más humanas y memorables.',
  image: 'https://cdn.magicpatterns.com/uploads/pQi8Ec16WWA9kjwxEpYUTa/BANCO_DE_PREGUNTAS_PARA_SESIONES_PROFUNDAS.png'
}, {
  num: 'IX',
  title: 'Máquina de Contenido Infinito',
  desc: 'Un año entero de ideas de contenido para posicionarte como astróloga.',
  image: 'https://cdn.magicpatterns.com/uploads/4XACVEbECn1BMGHMjQ4asS/MAQUINA_DE_CONTENIDO_INFINITO.png'
}, {
  num: 'X',
  title: 'Los 50 Casos Reales de Cartas Natales',
  desc: '50 cartas natales reales explicadas paso a paso por expertos.',
  image: 'https://cdn.magicpatterns.com/uploads/1aBAaDW9cCtQTmkTP8nNo9/los_50_casos_reales_de_caratas_natales.png'
}];
const slideVariants = {
  enter: () => ({
    opacity: 0
  }),
  center: {
    opacity: 1
  },
  exit: () => ({
    opacity: 0
  })
};
export function BonusSlider() {
  const [index, setIndex] = useState(0);
  const current = bonuses[index];
  const goTo = (next: number) => {
    setIndex((next + bonuses.length) % bonuses.length);
  };
  const renderTab = (b: Bonus, i: number) => {
    const active = i === index;
    return <button key={b.num} type="button" onClick={() => goTo(i)} className={'group relative flex items-center justify-center min-w-[3.25rem] md:min-w-[4rem] px-4 md:px-5 py-2.5 md:py-3 rounded-xl border backdrop-blur-sm transition-all ' + (active ? 'bg-primary/15 border-primary/60 shadow-[0_0_25px_rgba(212,168,87,0.25)]' : 'bg-card/40 border-border/50 hover:border-primary/40 hover:bg-card/70')} aria-pressed={active} aria-label={`Ver bonus ${b.num} — ${b.title}`} data-id="element-201" data-mp="12ag0vxgr5e1w">
        <span className={'font-heading font-black text-sm md:text-base transition-colors ' + (active ? 'text-primary' : 'text-foreground/70')} data-id="element-202" data-mp="3tayg691z8w43">
          {b.num}
        </span>
      </button>;
  };
  return <div className="relative" data-id="element-203" data-mp="3fja8qj2oavtg">
      {/* Tab strip — two centered rows */}
      <div className="flex flex-col items-center gap-2 md:gap-3 mb-10" data-id="element-204" data-mp="g1kbsuzro71k">
        <div className="flex justify-center gap-2 md:gap-3 flex-wrap px-2" data-id="element-205" data-mp="c19xmv1tmabm">
          {bonuses.slice(0, 5).map((b, i) => renderTab(b, i))}
        </div>
        <div className="flex justify-center gap-2 md:gap-3 flex-wrap px-2" data-id="element-206" data-mp="702nwqg4b12b">
          {bonuses.slice(5, 10).map((b, i) => renderTab(b, i + 5))}
        </div>
      </div>

      {/* Main slide area */}
      <div className="relative overflow-hidden rounded-3xl bg-card/60 border border-border/50 backdrop-blur-sm shadow-[0_0_40px_rgba(212,168,87,0.06)]" data-id="element-207" data-mp="2rtfvi0efz58o">
        {/* Prev / Next arrows */}
        <button type="button" onClick={() => goTo(index - 1)} aria-label="Bonus anterior" className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-background/70 border border-border/60 backdrop-blur-md text-foreground/80 hover:text-primary hover:border-primary/50 transition-all" data-id="element-208" data-mp="2cqo6yzmeljo8">
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" data-id="element-209" data-mp="2vfcln3uamsrk" />
        </button>
        <button type="button" onClick={() => goTo(index + 1)} aria-label="Próximo bonus" className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-background/70 border border-border/60 backdrop-blur-md text-foreground/80 hover:text-primary hover:border-primary/50 transition-all" data-id="element-210" data-mp="30de69t7db055">
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6" data-id="element-211" data-mp="1fz46lnbbpgbh" />
        </button>

        <div className="relative px-6 sm:px-10 md:px-16 py-12 md:py-14 min-h-[560px] md:min-h-[500px]" data-id="element-212" data-mp="17yxtu0j13ufd">
          <AnimatePresence mode="wait" initial={false} data-id="element-213" data-mp="130d1sodxkh20">
            <motion.div key={current.num} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            duration: 0.45,
            ease: [0.16, 1, 0.3, 1]
          }} className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-8 md:gap-12 items-center" data-id="element-214" data-mp="1hw2kacjwqc1z">
              {/* Bonus image */}
              <div className="relative flex items-center justify-center h-[19.8rem] md:h-[462px] bg-transparent" data-id="element-215" data-mp="20owms70cgbpb">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[78%] h-[78%] bg-primary/[0.08] rounded-full blur-3xl pointer-events-none" data-id="element-216" data-mp="1vq3bml8j4wsb"></div>
                {current.image ? <motion.img src={current.image} alt={`Bonus ${current.num} — ${current.title}`} loading="lazy" decoding="async" className="relative z-10 mx-auto h-full w-auto max-w-full object-contain bg-transparent drop-shadow-[0_25px_50px_rgba(212,168,87,0.28)]" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-217" data-mp="2oagqxghzrqww" /> : <div className="relative z-10 flex flex-col items-center justify-center text-center h-[85%] aspect-[3/4] rounded-xl border border-primary/25 bg-background/40 px-6 backdrop-blur-sm" data-id="element-218" data-mp="1wk2hpxkxb2rk">
                    <Sparkles className="w-8 h-8 text-primary/70 mb-4" strokeWidth={1.5} aria-hidden="true" data-id="element-219" data-mp="1mlwjbpybserg" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-primary/70 font-medium mb-3" data-id="element-220" data-mp="270wm3x0l07iw">
                      Bonus {current.num}
                    </span>
                    <span className="font-heading font-bold text-base md:text-lg text-foreground/85 leading-snug" data-id="element-221" data-mp="1y0rq0j0u8vk1">
                      {current.title}
                    </span>
                  </div>}
              </div>

              {/* Content */}
              <div data-id="element-222" data-mp="1o4y80k4co9uv">
                <div className="flex items-center gap-3 mb-4" data-id="element-223" data-mp="1jcn5d9i869ym">
                  <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary/10 border border-primary/30 font-heading font-black text-lg text-primary" data-id="element-224" data-mp="1jaae25qkun8w">
                    {current.num}
                  </div>
                </div>

                <p className="text-xs uppercase tracking-[0.3em] text-primary/80 font-medium mb-2" data-id="element-225" data-mp="15cvmaucuq3dr">
                  Biblioteca COSMOS™
                </p>
                <h3 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl leading-tight text-foreground mb-5" data-id="element-226" data-mp="10fvfuic3aym4">
                  {current.title}
                </h3>

                <p className="text-sm md:text-base text-foreground/85 leading-relaxed" data-id="element-227" data-mp="3gy4jmqq4r40y">
                  {current.desc}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 pb-7" data-id="element-228" data-mp="2tb64bwdqy24m">
          {bonuses.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a bonus ${i + 1}`} className={'h-1.5 rounded-full transition-all ' + (i === index ? 'w-8 bg-primary shadow-[0_0_10px_rgba(212,168,87,0.5)]' : 'w-1.5 bg-border hover:bg-primary/50')} data-id="element-229" data-mp="2yaj2vedzcxn1" data-mpi={i} />)}
        </div>
      </div>
    </div>;
}